/**
 * vs1_priority_sched (2026-10-18 · Node 09:52 「최근 일정 단지부터 만들어야 한다 — 그게 중요도 순」) — 이미지 생성·재생성 «일정 임박순» 정렬 (순수 · DB 0).
 *
 * 왜 — vx1_text_spec(규모 반영) 완료 294현장 중 공사중 252 · 분양예정 21 · 정비 13. 옛 정렬(v_site_image_queue.queue_score · job.priority)이
 *   공사중을 먼저 소진했다 → 청약 접수가 코앞인 현장(10/19 접수 7곳 등)이 뒤로 밀렸다.
 *
 * 정렬 키(앞이 먼저):
 *   ① 날짜가 있는 다음 일정(dated)이 있는 현장 — 남은 일수 오름차순.
 *      같은 일수면 일정 종류 순: 특공·일반 접수(apt_subscriptions) > 모델하우스 오픈 > 당첨발표·계약 > 분양예정 예상월.
 *      · 접수 중(시작 ≤ 오늘 ≤ 끝) = D-0.
 *      · 접수가 끝났으면 당첨발표·계약 시작·계약 끝 중 오늘 이후 첫 날짜(= 계약 끝까지 상위 유지). 계약도 끝났으면 ②의 공사중 그룹.
 *      · 분양예정 예상월(expected_sale_sort)은 모집공고(apt_subscriptions)가 아직 없는 현장만 — 공고가 나오면 공고 날짜가 이긴다.
 *   ② 날짜 없는 그룹(undated) — 그룹 순: 분양예정(예상월 경과 → 예상월 미상) · 당첨/계약 단계인데 일정 미상
 *      → 미분양 선착순 진행(lifecycle_stage unsold_active · preempt1 §6) → 정비 단계
 *      (관리처분 > 사업시행 > 시공사 선정 > 조합설립 > 구역지정·추진위) → 미분양 잔여(site_type unsold · 선착순 단계 아님) → 공사중·입주(그 밖 전부).
 *      ⚠️ 미분양 지역 집계 행(「경기 구리시 미분양」 · is_aggregate)은 적재 뷰(v_site_image_queue)가 이미 뺀다 — 여기 오는 건 실재 단지뿐.
 *   ③ 같으면 popularity_score 내림 → site_id(결정적).
 *
 * 정수 우선순위(image_jobs.priority · claim 은 priority 내림) — schedulePriority. 옛 값(최대 ~50만)보다 항상 크다:
 *   dated   1,000,000,000 − min(일수, 8999)×100,000 − 종류×10,000 + min(인기, 999)×10   (≥ 100,000,000)
 *   undated    90,000,000 − 그룹×1,000,000 + min(인기, 999)×10                          (≥ 60,000,000)
 *   임박 예외(vx.imminent_sites) IMMINENT_PRIORITY = 2,000,000,000.
 *   ×10 칸은 컷 순서(shotOffset 0..3)를 더할 자리다(적재기 — 옛 식의 (4 − shot_ord) 와 같은 뜻).
 */

export type ScheduleKind = 'subscription' | 'model_house' | 'winner_contract' | 'expected_sale' | 'redev' | 'unsold' | 'construction';

/** 같은 일수일 때의 종류 순(작을수록 먼저). */
export const SCHEDULE_KIND_RANK: Readonly<Record<ScheduleKind, number>> = {
  subscription: 0, model_house: 1, winner_contract: 2, expected_sale: 3, redev: 4, construction: 5, unsold: 6,
};

/** 정비 단계(lifecycle_stage) 순 — 관리처분 > 사업시행 > 시공사 선정 > 조합설립 > 구역지정·추진위. */
export const REDEV_STAGE_RANK: Readonly<Record<string, number>> = {
  mgmt_approved: 0, plan_approved: 1, constructor_selected: 2, union_established: 3, site_planning: 4,
};

/** undated 그룹 번호(작을수록 먼저). */
export const UNDATED_GROUP = {
  expected_sale_overdue: 0,
  expected_sale_unknown: 1,
  winner_contract_unknown: 2,
  /** preempt1 §6 — 미분양 선착순 진행(unsold_active): 지금 계약자가 찾는 현장 → 정비 단계보다 위 */
  unsold_first_come: 3,
  redev_base: 10,          // + REDEV_STAGE_RANK
  /** preempt1 §6 — 미분양 잔여(site_type unsold · 선착순 단계 아님 · 입주 후 제외): 공사중·입주 바로 위 */
  unsold_remaining: 19,
  construction: 20,
} as const;

/** 모집공고 1건의 날짜(YYYY-MM-DD · 없으면 null). */
export interface SubDates {
  spsplyBgn?: string | null;
  spsplyEnd?: string | null;
  rceptBgn?: string | null;
  rceptEnd?: string | null;
  winner?: string | null;
  contractBgn?: string | null;
  contractEnd?: string | null;
  modelHouseOpen?: string | null;
}

export interface ScheduleFacts {
  siteId: string;
  name?: string | null;
  lifecycleStage: string | null;
  popularity: number | null;
  /** 이 현장에 이어진 모집공고(묶음 단지면 여럿). */
  subs: readonly SubDates[];
  modelHouseOpen?: string | null;
  /** apt_sites.expected_sale_sort(분양예정 예상월의 정렬용 날짜) */
  expectedSaleSort?: string | null;
  /** apt_sites.site_type — 미분양 잔여 그룹 판정(preempt1 §6). 없으면 종전 판정. */
  siteType?: string | null;
}

export interface ScheduleKey {
  siteId: string;
  kind: ScheduleKind;
  /** 다음 일정 날짜(dated 만) */
  date: string | null;
  /** 남은 일수(dated 만 · 접수 중 0) */
  days: number | null;
  dated: boolean;
  /** undated 그룹(dated 면 -1) */
  group: number;
  popularity: number;
}

/** '20261019' · '2026-10-19…' → '2026-10-19'. 못 읽으면 null. */
export function normYmd(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  const m8 = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m8) return `${m8[1]}-${m8[2]}-${m8[3]}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** to − from (일). UTC 자정 고정. */
export function daysUntil(today: string, date: string): number {
  return Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
}

const minDate = (xs: ReadonlyArray<string | null>): string | null => xs.filter((x): x is string => !!x).sort()[0] ?? null;
const maxDate = (xs: ReadonlyArray<string | null>): string | null => xs.filter((x): x is string => !!x).sort().at(-1) ?? null;

interface Ev { kind: ScheduleKind; date: string }

/** 모집공고 1건 → 오늘 이후(또는 진행 중) 일정 1건. 접수 전·중 = subscription · 접수 뒤 계약 끝까지 = winner_contract · 그 뒤 null. */
export function subEventOf(s: SubDates, today: string): Ev | null {
  const appStart = minDate([normYmd(s.spsplyBgn), normYmd(s.rceptBgn)]);
  const appEnd = maxDate([normYmd(s.rceptEnd), normYmd(s.spsplyEnd), appStart]);
  if (appEnd && appEnd >= today) return { kind: 'subscription', date: appStart && appStart > today ? appStart : today };
  const next = [normYmd(s.winner), normYmd(s.contractBgn), normYmd(s.contractEnd)].filter((d): d is string => !!d && d >= today).sort()[0];
  return next ? { kind: 'winner_contract', date: next } : null;
}

const pop = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** 현장 1곳의 정렬 키. today = KST 'YYYY-MM-DD'. */
export function scheduleKeyOf(f: ScheduleFacts, today: string): ScheduleKey {
  const evs: Ev[] = [];
  for (const s of f.subs) {
    const e = subEventOf(s, today);
    if (e) evs.push(e);
    const mh = normYmd(s.modelHouseOpen);
    if (mh && mh >= today) evs.push({ kind: 'model_house', date: mh });
  }
  const mh = normYmd(f.modelHouseOpen);
  if (mh && mh >= today) evs.push({ kind: 'model_house', date: mh });
  const es = normYmd(f.expectedSaleSort);
  if (!f.subs.length && es && es >= today) evs.push({ kind: 'expected_sale', date: es });
  const base = { siteId: f.siteId, popularity: pop(f.popularity) };
  if (evs.length) {
    evs.sort((a, b) => a.date.localeCompare(b.date) || SCHEDULE_KIND_RANK[a.kind] - SCHEDULE_KIND_RANK[b.kind]);
    const e = evs[0];
    return { ...base, kind: e.kind, date: e.date, days: Math.max(0, daysUntil(today, e.date)), dated: true, group: -1 };
  }
  const stage = f.lifecycleStage ?? '';
  const undated = (kind: ScheduleKind, group: number): ScheduleKey => ({ ...base, kind, date: null, days: null, dated: false, group });
  if (!f.subs.length && stage === 'pre_announcement') {
    return es ? undated('expected_sale', UNDATED_GROUP.expected_sale_overdue) : undated('expected_sale', UNDATED_GROUP.expected_sale_unknown);
  }
  if (!f.subs.length && (stage === 'award_pending' || stage === 'award_announced')) return undated('winner_contract', UNDATED_GROUP.winner_contract_unknown);
  if (stage === 'unsold_active') return undated('unsold', UNDATED_GROUP.unsold_first_come);
  if (stage in REDEV_STAGE_RANK) return undated('redev', UNDATED_GROUP.redev_base + REDEV_STAGE_RANK[stage]);
  if (f.siteType === 'unsold' && stage !== 'post_move_in') return undated('unsold', UNDATED_GROUP.unsold_remaining);
  return undated('construction', UNDATED_GROUP.construction);
}

/** 정렬(앞이 먼저). */
export function compareScheduleKey(a: ScheduleKey, b: ScheduleKey): number {
  if (a.dated !== b.dated) return a.dated ? -1 : 1;
  if (a.dated) {
    const d = (a.days ?? 0) - (b.days ?? 0);
    if (d) return d;
    const k = SCHEDULE_KIND_RANK[a.kind] - SCHEDULE_KIND_RANK[b.kind];
    if (k) return k;
  } else if (a.group !== b.group) return a.group - b.group;
  return b.popularity - a.popularity || a.siteId.localeCompare(b.siteId);
}

export const IMMINENT_PRIORITY = 2_000_000_000;

/**
 * 임박 예외 작업의 priority — 예외 안에서도 일정 임박순(D-1 현장이 D-8 현장보다 먼저)을 지킨다.
 *   IMMINENT_PRIORITY + ⌊일정 정수 / 100⌋ × 10 + 컷 순서 (최대 ≈ 2.1e9 < int4 상한 2,147,483,647).
 */
export function imminentPriorityOf(basePriority: number | null | undefined, shot: string | null | undefined): number {
  const b = Math.min(1_000_000_000, Math.max(0, Math.floor(Number(basePriority) || 0)));
  return IMMINENT_PRIORITY + Math.floor(b / 100) * 10 + shotOffset(shot);
}

/** 정수 우선순위(claim 은 priority 내림). 컷 순서 자리(×10 칸 아래 0..9)는 비워 둔다. */
export function schedulePriority(k: ScheduleKey): number {
  const p = Math.min(999, Math.floor(k.popularity)) * 10;
  if (k.dated) return 1_000_000_000 - Math.min(8999, Math.max(0, k.days ?? 0)) * 100_000 - SCHEDULE_KIND_RANK[k.kind] * 10_000 + p;
  return 90_000_000 - Math.min(29, Math.max(0, k.group)) * 1_000_000 + p;
}

const SHOT_ORDER = ['S1_aerial_day', 'S2_aerial_golden', 'S3_eye_level', 'S4_night'];
/** 컷 순서 가산(S1 3 · S2 2 · S3 1 · S4 0 · 그 밖 0) — 옛 식 (4 − shot_ord) 와 같은 뜻. */
export function shotOffset(shot: string | null | undefined): number {
  const i = SHOT_ORDER.indexOf(String(shot ?? ''));
  return i < 0 ? 0 : 3 - i;
}

/** 표기용 「청약 접수 D-1」 같은 한 줄. */
export function scheduleLabelOf(k: ScheduleKey): string {
  const name: Record<ScheduleKind, string> = {
    subscription: '청약 접수', model_house: '모델하우스 오픈', winner_contract: '당첨발표·계약', expected_sale: '분양예정',
    redev: '정비', unsold: '미분양', construction: '공사중·입주',
  };
  if (k.dated) return `${name[k.kind]} ${k.date} D-${k.days}`;
  const g = k.group;
  if (g === UNDATED_GROUP.expected_sale_overdue) return '분양예정(예상월 경과)';
  if (g === UNDATED_GROUP.expected_sale_unknown) return '분양예정(시기 미상)';
  if (g === UNDATED_GROUP.winner_contract_unknown) return '당첨·계약(일정 미상)';
  if (g === UNDATED_GROUP.unsold_first_come) return '미분양 선착순';
  if (g === UNDATED_GROUP.unsold_remaining) return '미분양 잔여';
  if (k.kind === 'redev') {
    const stage = Object.entries(REDEV_STAGE_RANK).find(([, r]) => r === g - UNDATED_GROUP.redev_base)?.[0];
    const ko: Record<string, string> = { mgmt_approved: '관리처분', plan_approved: '사업시행', constructor_selected: '시공사 선정', union_established: '조합설립', site_planning: '구역지정·추진위' };
    return `정비 ${ko[stage ?? ''] ?? ''}`.trim();
  }
  return name.construction;
}

/** 여러 현장을 정렬해 키 목록으로(보고·선택용). */
export function rankSites(facts: readonly ScheduleFacts[], today: string): ScheduleKey[] {
  return facts.map((f) => scheduleKeyOf(f, today)).sort(compareScheduleKey);
}

/**
 * instr/cl/order_by_schedule (Node 12:02 · 상시 원칙) — 현장 단위 큐·배치의 «공용» 정렬(순수 · 안정).
 *   키가 있는 항목 = compareScheduleKey 순(일수 → 종류 → 날짜 없는 그룹 → 인기 → id) · 키가 없는 항목 = 뒤(입력 순서 유지).
 *   같은 현장의 여러 항목(키워드·컷 등)은 입력 순서 그대로 붙어 나온다.
 */
export function orderBySchedule<T>(items: readonly T[], idOf: (t: T) => string | null | undefined, keys: ReadonlyMap<string, ScheduleKey>): T[] {
  return items
    .map((t, i) => ({ t, i, k: (() => { const id = idOf(t); return id ? keys.get(id) : undefined; })() }))
    .sort((a, b) => {
      if (a.k && b.k) return compareScheduleKey(a.k, b.k) || a.i - b.i;
      if (a.k || b.k) return a.k ? -1 : 1;
      return a.i - b.i;
    })
    .map((x) => x.t);
}
